import type { ApiAssistantRange, ApiAssistantTripCost } from 'src/types/assistant';

import { formatMoney, formatAmount } from 'src/utils/money';

import { Fuel } from 'src/types/car';

/**
 * The Trip cost line under a car for rent (api#430, the spec §3.5): the booking's quote, the
 * kilometres and the fuel — every number the platform's, written here as one English sentence. What
 * the platform could not count is said in words, never guessed (Tim, 2026-10-29: an engine the
 * official data does not rate is "we have not yet integrated with European providers").
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-10-26` → "26 Oct", the same on every device: no locale, no time zone. */
const dayWords = (day: string | null): string | undefined => {
  const match = day ? /^\d{4}-(\d{2})-(\d{2})$/.exec(day) : null;
  const month = match ? MONTHS[Number(match[1]) - 1] : undefined;

  return match && month ? `${Number(match[2])} ${month}` : undefined;
};

/** "155" or "140–190": one figure, or a range when the engine is not stated. */
const range = (value: ApiAssistantRange, digits = 0): string =>
  value.min === value.max
    ? formatAmount(value.min, digits)
    : `${formatAmount(value.min, digits)}–${formatAmount(value.max, digits)}`;

/** What a car of this fuel fills up with, as the bulletin prices it: every hybrid runs on petrol. */
const fuelWords = (fuel: Fuel | null): string => {
  switch (fuel) {
    case Fuel.DIESEL:
      return 'diesel';
    case Fuel.LPG:
      return 'LPG';
    default:
      return 'petrol';
  }
};

const fuelPart = (trip: ApiAssistantTripCost): string => {
  const { fuel } = trip;

  switch (fuel.status) {
    case 'km_not_stated':
      return 'fuel: tell the assistant how many km a day to count it';
    case 'fuel_not_stated':
      return 'fuel not counted: the dealer states no fuel type';
    case 'not_integrated':
      return 'fuel not counted: we have not yet integrated with European providers of car checks by VIN';
    case 'unavailable':
      return 'fuel could not be checked right now';
    default:
      break;
  }

  if (!fuel.amount || !fuel.per100Km) {
    return 'fuel could not be checked right now';
  }

  if (fuel.unit === 'kWh') {
    return `about ${range(fuel.amount)} kWh (${range(fuel.per100Km, 1)} kWh/100 km, US EPA); charging not counted: no source of charging prices`;
  }

  const litres = `fuel about ${range(fuel.amount)} l of ${fuelWords(fuel.fuel)} (${range(fuel.per100Km, 1)} l/100 km, US EPA)`;
  const petrolOnly = fuel.petrolOnly ? ', on petrol only: charging lowers it' : '';
  const price = fuel.price;

  if (price?.status === 'counted' && price.cost && price.country) {
    const date = dayWords(price.date);

    return `${litres}${petrolOnly} ≈ ${range(price.cost)} EUR at prices in ${price.country.name}${date ? ` of ${date}` : ''}`;
  }

  const country = price?.country?.name;
  const why =
    price?.status === 'not_in_bulletin'
      ? `the fuel price in ${country ?? 'this country'} is not known to the platform`
      : price?.status === 'place_unknown'
        ? 'the fuel price of the pickup place is not known to the platform'
        : 'fuel prices could not be checked right now';

  return `${litres}${petrolOnly}; ${why}`;
};

/** The whole line: the booking, then the fuel, then the kilometres over the car's limit when they are. */
export const tripCostText = (trip: ApiAssistantTripCost): string => {
  const days = `${trip.days} ${trip.days === 1 ? 'day' : 'days'}`;
  const distance =
    trip.kmPerDay != null && trip.km != null
      ? `, ${formatAmount(trip.kmPerDay)} km a day (${formatAmount(trip.km)} km)`
      : '';
  const booking =
    `${days}${distance}: pay ${formatMoney(trip.payNow, trip.currency)} now — rental ${formatAmount(trip.rental)}, ` +
    `platform fee ${formatAmount(trip.platformFee)}, deposit ${formatAmount(trip.deposit)} comes back`;
  const over =
    trip.overKmLimit != null && trip.kmPerDay != null
      ? `. ${formatAmount(trip.kmPerDay)} km a day is above this car’s limit of ${formatAmount(trip.overKmLimit)} km; the listing states no price for extra kilometres`
      : '';

  return `${booking}; ${fuelPart(trip)}${over}`;
};
